// Enhanced Dashboard page with Display Summary API integration and permission-based UI control
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatusBadge, LoadingSpinner, Button } from '../components/Common';
import { CheckpointCard } from '../components/CheckpointCard';
import { useAuthContext as useAuth } from '../contexts/AuthContext';
import { canView, canUpdate, getAccessLevel, permissionChecks } from '../lib/auth/helpers';
import { PermissionGuard } from '../lib/auth/helpers';
import { 
  useDashboardStats,
  useCheckpointGridData,
  useDashboardLoading,
  useDashboardError,
  useDashboardActions,
  useDashboardHash,
  useDashboardLastFetch
} from '../store/dashboardMasterStore';
import { getRelativeTime, formatHawaiiDateTime } from '../utils/helpers';
import { useApi } from '../hooks/useApi';
import { useDashboardMasterAuth } from '../hooks/useDashboardMasterAuth';
import { displaySummaryService } from '../services/displaySummaryService';
import toast from 'react-hot-toast';
import { 
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CubeIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { getCurrentConfig } from '../config';
import { TerminalDisplaySwitch } from '../components/TerminalDisplaySwitch';


export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, getAccessToken } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [toggleStates, setToggleStates] = useState<Record<number, boolean>>({});
  const [toggling, setToggling] = useState<Record<number, boolean>>({});
  const [beltsCount, setBeltsCount] = useState(0);
  const [queueTimes, setQueueTimes] = useState<Record<number, any[]>>({});
  const [checkpointStatuses, setCheckpointStatuses] = useState<Record<number, { isOpen: boolean; isException: boolean; totalLanes: number }>>({});
  const [lastQueueUpdate, setLastQueueUpdate] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingQueues, setIsLoadingQueues] = useState(false);
  const [isDisplayShutdown, setIsDisplayShutdown] = useState(false);
  
  const queueUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadQueueTimesRef = useRef<() => Promise<void>>();

  const api = useApi();
  const dashboardMasterApi = useDashboardMasterAuth();

  const stats = useDashboardStats();
  const checkpointGridData = useCheckpointGridData();
  const isLoading = useDashboardLoading();
  const error = useDashboardError();
  const currentHash = useDashboardHash();
  const lastFetch = useDashboardLastFetch();
  const { fetchDashboardData, forceRefresh, startPeriodicUpdates, checkForUpdates } = useDashboardActions();

  const currentConfig = getCurrentConfig();

  // useEffect(() => {
  //   if (checkpointGridData.length > 0) {
  //     const initialStates: Record<number, boolean> = {};
  //     checkpointGridData.forEach(checkpoint => {
  //       initialStates[checkpoint.id] = checkpoint.isOpen;
  //     });
  //     setToggleStates(initialStates);
  //   }
  // }, [checkpointGridData]);

  // Initialize toggleStates ONCE from Dashboard Master on first load
  // After initialization, Display Summary API takes over for all status updates
  useEffect(() => {
    if (checkpointGridData.length > 0 && Object.keys(toggleStates).length === 0) {
      const initialStates: Record<number, boolean> = {};
      checkpointGridData.forEach(checkpoint => {
        initialStates[checkpoint.id] = checkpoint.isOpen;
      });
      setToggleStates(initialStates);
    }
  }, [checkpointGridData]);

  // Update toggleStates from Display Summary API (every 30 seconds)
  // This is the primary source of truth for checkpoint status after initial load
  useEffect(() => {
    if (Object.keys(checkpointStatuses).length > 0) {
      setToggleStates(prev => {
        const newStates: Record<number, boolean> = { ...prev };
        Object.entries(checkpointStatuses).forEach(([checkpointId, status]) => {
          const id = parseInt(checkpointId, 10);
          // Always update from Display Summary unless currently toggling
          if (!toggling[id]) {
            newStates[id] = status.isOpen;
          }
        });
        return newStates;
      });
    }
  }, [checkpointStatuses, toggling]);

  const scheduleNextQueueUpdate = useCallback(() => {
    if (queueUpdateTimeoutRef.current) {
      clearTimeout(queueUpdateTimeoutRef.current);
    }
    
    queueUpdateTimeoutRef.current = setTimeout(() => {
      loadQueueTimesRef.current?.();
    }, 30000);
  }, []);

  const loadQueueTimesFromAPI = useCallback(async () => {
    if (isLoadingQueues) {
      return;
    }
    
    setIsLoadingQueues(true);
    try {
      const { queueTimes: apiQueueTimes, checkpointStatuses: apiStatuses, lastUpdated, displaySwitch } = 
        await displaySummaryService.getAllQueueTimesFromAPI();
      
      setQueueTimes(apiQueueTimes);
      setCheckpointStatuses(apiStatuses);
      setLastQueueUpdate(lastUpdated);
      setIsDisplayShutdown(!displaySwitch);
      
      if (isInitialized) {
        scheduleNextQueueUpdate();
      }
      
    } catch (error) {
      console.error('Failed to load queue times from API:', error);
      toast.error('Failed to load queue times from API');
      
      const emptyQueueTimes: Record<number, any[]> = {};
      checkpointGridData.forEach(checkpoint => {
        emptyQueueTimes[checkpoint.id] = [];
      });
      setQueueTimes(emptyQueueTimes);
      setIsDisplayShutdown(true);
      
      if (isInitialized) {
        scheduleNextQueueUpdate();
      }
    } finally {
      setIsLoadingQueues(false);
    }
  }, [isLoadingQueues, checkpointGridData, isInitialized, scheduleNextQueueUpdate]);

  loadQueueTimesRef.current = loadQueueTimesFromAPI;

  useEffect(() => {
    if (checkpointGridData.length > 0 && !isInitialized) {
      setIsInitialized(true);
      loadQueueTimesFromAPI();
    }
  }, [checkpointGridData.length, isInitialized, loadQueueTimesFromAPI]);

  useEffect(() => {
    if (!getAccessToken) {
      return;
    }
    
    fetchDashboardData();
    const stopUpdates = startPeriodicUpdates(2);

    return () => {
      stopUpdates();
    };
  }, [getAccessToken, fetchDashboardData, startPeriodicUpdates]);

  useEffect(() => {
    if (!isInitialized) return;
    
    scheduleNextQueueUpdate();

    return () => {
      if (queueUpdateTimeoutRef.current) {
        clearTimeout(queueUpdateTimeoutRef.current);
      }
    };
  }, [isInitialized, scheduleNextQueueUpdate]);

  useEffect(() => {
    const loadBeltsCount = async () => {
      try {
        const response = await fetch(`/belts.json?v=${Date.now()}`);
        const beltsData = await response.json();
        setBeltsCount(beltsData.length);
      } catch (error) {
        // Silent fail
      }
    };

    loadBeltsCount();
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const canViewCheckpoints = canView('checkpoint');
  const canUpdateCheckpoints = canUpdate('checkpoint');
  const canViewLanes = canView('lane');
  const canUpdateLanes = canUpdate('lane');
  
  const canViewTerminalTile = permissionChecks.canViewTerminalTile();
  const canViewCheckpointTile = permissionChecks.canViewCheckpointTile();
  const canViewQueueTile = permissionChecks.canViewQueueTile();
  const canViewLaneTile = permissionChecks.canViewLaneTile();
  const canUpdateCheckpointStatus = permissionChecks.canUpdateCheckpointStatus();
  
  const canViewBeltPage = permissionChecks.canViewBeltPage();

  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await Promise.all([
        forceRefresh(),
        loadQueueTimesFromAPI()
      ]);
      toast.success('Dashboard refreshed successfully');
    } catch (err) {
      console.error('❌ Refresh failed:', err);
      toast.error('Failed to refresh dashboard');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCheckpointClick = (checkpointId: number) => {
    navigate(`/checkpoint/${checkpointId}/lanes`);
  };

  const handleToggleStatus = async (e: React.MouseEvent, checkpointId: number) => {
    e.stopPropagation();
    
    if (!canUpdateCheckpointStatus) {
      toast.error('You do not have permission to toggle checkpoint status');
      return;
    }
    
    if (toggling[checkpointId]) return;
    
    const newStatus = !toggleStates[checkpointId];
    const originalStatus = toggleStates[checkpointId];
    
    setToggleStates(prev => ({
      ...prev,
      [checkpointId]: newStatus
    }));
    
    setToggling(prev => ({
      ...prev,
      [checkpointId]: true
    }));

    try {
      const response = await api.toggleCheckpoint(checkpointId,user);
      
      toast.success(
        `Checkpoint ${newStatus ? 'opened' : 'closed'} successfully!`
      );
      
      // Refresh both backend and display-api data sequentially
      await forceRefresh();
      await loadQueueTimesFromAPI();
      
    } catch (err) {
      console.error(`❌ Toggle failed for checkpoint ${checkpointId}:`, err);
      
      setToggleStates(prev => ({
        ...prev,
        [checkpointId]: originalStatus
      }));
      
      await Promise.all([
        forceRefresh(),
        loadQueueTimesFromAPI()
      ]);
      
      toast.error('Failed to toggle checkpoint status');
    } finally {
      setToggling(prev => ({
        ...prev,
        [checkpointId]: false
      }));
    }
  };

  if (isLoading && !checkpointGridData.length) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* Watermark overlay when display is shutdown */}
      {isDisplayShutdown && (
        <div className="fixed inset-0 flex items-start justify-center pt-16 pointer-events-none z-50">
          <div className="flex flex-col items-center opacity-40">
            <ExclamationTriangleIcon className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 lg:h-56 lg:w-56 text-yellow-500" />
            <div className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-yellow-500 mt-4 sm:mt-6 md:mt-8 tracking-widest text-center px-4">
              DISPLAY SHUTDOWN
            </div>
          </div>
        </div>
      )}
      
      {/* Rest of content with relative positioning */}
      <div className="relative z-10">
        <Header />
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">Airport Operations Dashboard</h1>
                <p className="mt-2 text-gray-400">
                  Real-time checkpoint and lane status monitoring
                </p>
              </div>
              <PermissionGuard feature="dashboard" action="any">
                <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                  {/* Display Switch Button - Shows only for users with admin_display_switch permission */}
                  <TerminalDisplaySwitch 
                    terminalId={1}
                    onToggle={(newState) => {
                      console.log('Display switch toggled:', newState);
                      setIsDisplayShutdown(!newState); // When display is OFF (false), show warning
                    }}
                  />
                  
                  {/* Refresh Button */}
                  <Button
                    onClick={handleRefresh}
                    variant="primary"
                    size="sm"
                    disabled={refreshing || isLoading}
                    className="flex items-center"
                  >
                    <ArrowPathIcon className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>
              </PermissionGuard>
            </div>
          </div>

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {canViewTerminalTile && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-8 w-8 text-blue-400" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-400">Terminals</p>
                      <p className="text-2xl font-bold text-white">{stats.terminals.total}</p>
                      <p className="text-sm text-green-400">
                        {stats.terminals.open} open
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {canViewCheckpointTile && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-8 w-8 text-green-400" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-400">Checkpoints</p>
                      <p className="text-2xl font-bold text-white">{stats.checkpoints.total}</p>
                      <p className="text-sm text-green-400">
                        {stats.checkpoints.open} open
                      </p>
                      {stats.checkpoints.withExceptions > 0 && (
                        <p className="text-sm text-orange-400">
                          {stats.checkpoints.withExceptions} with exceptions
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {canViewQueueTile && (
                <div className="bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center">
                    <ClockIcon className="h-8 w-8 text-yellow-400" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-400">Queue</p>
                      <p className="text-2xl font-bold text-white">{stats.laneAssignments.total}</p>
                      <p className="text-sm text-green-400">
                        {stats.laneAssignments.open} open
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {canViewLaneTile && (
                <div 
                  onClick={canViewBeltPage ? () => navigate('/belts') : undefined}
                  className={`bg-gray-800 rounded-lg p-6 transition-colors ${
                    canViewBeltPage 
                      ? 'cursor-pointer hover:bg-gray-750' 
                      : 'cursor-default opacity-75'
                  }`}
                >
                  <div className="flex items-center">
                    <CubeIcon className="h-8 w-8 text-orange-400" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-400">Lanes</p>
                      <p className="text-2xl font-bold text-white">{beltsCount}</p>
                      <p className="text-sm text-green-400">
                        {beltsCount} available
                      </p>
                      {!canViewBeltPage && (
                        <p className="text-sm text-gray-500 mt-1">
                          Read-only access
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Security Checkpoints</h2>
          </div>

          {checkpointGridData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No checkpoints available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {checkpointGridData.map((checkpoint) => (
                <CheckpointCard
                  key={checkpoint.id}
                  id={checkpoint.id}
                  name={checkpoint.name}
                  description={checkpoint.description}
                  terminalName={checkpoint.terminalName}
                  isOpen={
                    toggleStates[checkpoint.id] !== undefined 
                      ? toggleStates[checkpoint.id]
                      : checkpointStatuses[checkpoint.id]?.isOpen ?? checkpoint.isOpen
                  }
                  isException={checkpointStatuses[checkpoint.id]?.isException ?? checkpoint.isException}
                  sectionsCount={checkpoint.sectionsCount}
                  assetsCount={checkpoint.assetsCount}
                  queueCount={checkpoint.laneAssignmentsCount}
                  lastUpdated={checkpoint.last_updated}
                  updatedBy={checkpoint.updated_by}
                  queueTimes={queueTimes[checkpoint.id] || []}
                  isToggling={toggling[checkpoint.id]}
                  isDisplayShutdown={isDisplayShutdown}
                  onToggleStatus={handleToggleStatus}
                  onClick={handleCheckpointClick}
                />
              ))}
            </div>
          )}

        </main>
      </div>
    </div>
  );
};